import 'blockadblock';
import React from 'react';
import ReactDOM from 'react-dom';
import { AdSlot, DFPManager } from 'react-dfp';
import {debounce} from 'throttle-debounce';
import onVisibilityChange, {isElementAtLeastPartiallyVisible} from 'utils/visibility';

const SCROLL_BUFFER_PIXELS = 200;  // Buffer value added to the top and bottom of the actual
                                   // viewport so that ads will be loaded in before they come into view

/**
 * React component to wrap the `react-dfp` AdSlot component. Adds functionality to dynamically create and destroy itself to accomodate Ad Managers new
 * long-lived page views system.
 */
export default class DynamicAdSlot extends React.Component {
    constructor (props) {
        super(props);
        this.setSize = this.setSize.bind(this);
        this.collapseContainer = this.collapseContainer.bind(this);

        let width = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);

        // Find the size of the ad that should be used based on the breakpoint we are currently on.
        let matchedSizeMapping = null;
        for (let sizeMapping of this.props.sizeMapping) {
            // Find the biggest size mapping that is smaller than the width of the page.
            if (sizeMapping.viewport[0] <= width) {
                if (matchedSizeMapping !== null) {
                    if (sizeMapping.viewport[0] > matchedSizeMapping.viewport[0]) {
                        matchedSizeMapping = sizeMapping;
                    }
                } else {
                    matchedSizeMapping = sizeMapping;
                }
            }
        }

        let initialAssumedAdSize = this.props.sizes[0];
        if (matchedSizeMapping !== null) {
            initialAssumedAdSize = matchedSizeMapping.sizes[0];
        }

        this.state = {
            showAd: false,
            lastAdSize: this.setSize(initialAssumedAdSize, false),
            scrollReload: true
        };
    }

    /**
     * Updates the last known ad size that was displayed. This is to prevent the page from dropping around when ads are created
     * and destroyed.
     *
     * @param {Array} size - A two element array of integers where the first value is the width and the second is the height.
     * @param {boolean} updateState - Whether or not the state value for the last known ad size should be set. Should only be
     *  false when calling this function before the component can be mounted.
     * @returns {Array} An array containing the width and the height of the ad.
     */
    setSize(size, updateState=true) {
        if (typeof size === 'string') {
            size = [0, 0];
        }
        if (updateState){
            this.setState({
                lastAdSize: size,
            });
        }
        return size;
    }

    /**
     * Collapse the react element so that it doesn't take up any space on the page.
     *
     * @param {boolean} collapse - Set true to remove all width, height, padding and margin, false to revert back to normal.
     */
    collapseContainer(collapse=true){
        let container = ReactDOM.findDOMNode(this);
        if (collapse) {
            container.style.padding = 0;
            container.style.margin = 0;
            container.style.height = 0;
            container.style.width = 0;
            this.setSize([0, 0]);
        } else {
            container.style.removeProperty('padding');
            container.style.removeProperty('margin');
            container.style.removeProperty('height');
            container.style.removeProperty('width');
        }
    }

    componentDidMount() {
        // Turn off the scroll reloading the ads when there is an ad blocker present. Otherwise the page will jump
        // around when we start fighting the ad blocker.
        new window.BlockAdBlock({
            checkOnLoad: true,
            resetOnEnd: false,
            debug: false
        })
        .onDetected(() => this.setState({scrollReload: false}));

        // Collapse the container before the ad loads.
        if (this.props.startCollapsed) {
            this.collapseContainer();
        }

        // Check to see if the ad should be shown as soon as it is loaded.
        if (isElementAtLeastPartiallyVisible(ReactDOM.findDOMNode(this))) {
            this.setState({showAd: true});
        }

        // Listen for scroll events so that we can handle whether or not the ad is now visible.
        window.addEventListener('scroll', debounce(25, () => {
            if (this.state.scrollReload) {
                // Watch for when the ad comes into view
                let container = ReactDOM.findDOMNode(this);
                onVisibilityChange(container, (isVisible) => {
                    let adSlotElement = container.firstChild;
                    let adSlot = null;
                    if (adSlotElement && adSlotElement.id){
                        adSlot = DFPManager.getRegisteredSlots()[adSlotElement.id];
                    }

                    if (isVisible) {
                        if (!this.state.showAd) {
                            this.setState({showAd: true});
                        }
                    } else if (adSlot) {
                        DFPManager.getGoogletag().then(googletag => {
                            googletag.destroySlots([adSlot.gptSlot]);
                            // Causes the destroy slot to be honored sooner, so the destroyed slots creative can be reused sooner.
                            DFPManager.refresh();
                        }).catch((error) => console.error("Unable to destroy ad slot", adSlot, "Error:", error));
                        this.setState({showAd: false});
                    }
                }, 'partial', SCROLL_BUFFER_PIXELS - this.state.lastAdSize[1])();
            }
        }), {passive: true});
    }

    render() {
        const Component = this.state.showAd ? AdSlot : 'div';

        // Built out which properties to use.
        let props = {};
        if (this.state.showAd){
            // Properties that are passed to the `AdSlot` component.
            props = {
                sizes: this.props.sizes,
                sizeMapping: this.props.sizeMapping,
                dfpNetworkId: this.props.dfpNetworkId,
                adUnit: this.props.adUnit,
                targetingArguments: this.props.targetingArguments,
                fetchNow: true,
                /**
                 * Calls to `DFPManager.refresh()` will only refresh ads that failed to serve an ad.
                 */
                shouldRefresh: (slot) => {
                    let adSlot = DFPManager.getRegisteredSlots()[slot.slotId];
                    return adSlot.gptSlot && adSlot.gptSlot.getResponseInformation() == null;
                },
                /**
                 * When the ad slot has finished rendering update the last known size of the ad, so that when it is destroyed
                 * the page does not move around.
                 */
                onSlotRender: async (eventData) => {
                    let adSlotDiv = document.getElementById(eventData.slotId);
                    let event = eventData.event;
                    if (event && !event.isEmpty) {
                        this.collapseContainer(false);
                        let size = [0, 0];  // will become the width and height to use for the ad slot after the ad is destroyed.
                        if (event.size !== null){
                            // Use the size the Google says it is
                            size = event.size;
                        }
                        // If size is [0, 0] use the width of the element instead, this will be true for all `fluid` ad sizes.
                        if (typeof size !== 'string' && size.length == 2 && size[0] == 0 && size[1] == 0){
                            await sleep(500);
                            size = [adSlotDiv.offsetWidth, adSlotDiv.offsetHeight];
                        }

                        this.setSize(size);
                    } else {
                        // Collapse any ads that don't find an ad to display
                        if (event.slot.getResponseInformation() == null){
                            this.collapseContainer();
                        }
                    }
                }
            }
        }

        props.style = {
            width: this.state.lastAdSize[0],
            height: this.state.lastAdSize[1]
        };
        return  (
            <Component {...props}/>
        );
    }
}

function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}
